/**
 * UserAttendanceModal.js
 * Modal for displaying user's attendance in a specific group
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Table,
  Badge,
  Alert,
  Spinner,
  Form,
  Row,
  Col
} from 'react-bootstrap';
import { userService } from '../../services/api';

const UserAttendanceModal = ({ show, onHide, userId, group }) => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // Month options
  const months = [
    { value: 1, label: 'Gennaio' },
    { value: 2, label: 'Febbraio' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Aprile' },
    { value: 5, label: 'Maggio' },
    { value: 6, label: 'Giugno' },
    { value: 7, label: 'Luglio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Settembre' },
    { value: 10, label: 'Ottobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Dicembre' }
  ];

  // Initialize with current month/year
  useEffect(() => {
    if (show && !selectedMonth && !selectedYear) {
      const now = new Date();
      setSelectedMonth(now.getMonth() + 1); // getMonth() returns 0-11
      setSelectedYear(now.getFullYear());
    }
  }, [show, selectedMonth, selectedYear]);

  // Load user attendance
  const loadAttendance = useCallback(async () => {
    if (!userId || !group?.id || !selectedMonth || !selectedYear) return;

    try {
      setLoading(true);
      setError('');
      
      const response = await userService.getUserAttendance(
        userId, 
        group.id, 
        selectedMonth, 
        selectedYear
      );
      
      if (response.success) {
        setAttendance(response.data);
      } else {
        setError(response.message || 'Errore nel caricamento delle presenze');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading user attendance:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, group?.id, selectedMonth, selectedYear]);

  // Handle month change
  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  // Handle year change  
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get year options (current year ± 2)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = -2; i <= 2; i++) {
      years.push(currentYear + i);
    }
    return years;
  };

  // Calculate statistics
  const totalAttendance = attendance.length;
  const presentCount = attendance.filter(a => a.isPresent).length;
  const absentCount = totalAttendance - presentCount;

  // Effects
  useEffect(() => {
    if (show && selectedMonth && selectedYear) {
      loadAttendance();
    }
  }, [loadAttendance, show, selectedMonth, selectedYear]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setAttendance([]);
      setError('');
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-calendar-check me-2"></i>
          Presenze - {group?.name}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {/* Group Info */}
        <div className="mb-4 p-3 bg-light rounded">
          <h6 className="mb-2">
            <strong>Gruppo:</strong> {group?.name}
          </h6>
          <div className="text-muted">
            <strong>Corso:</strong> {group?.course?.name} - 
            Anno Sportivo {group?.course?.academicYear}/{group?.course?.academicYear + 1}
          </div>
        </div>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Mese</Form.Label>
              <Form.Select
                value={selectedMonth}
                onChange={handleMonthChange}
                disabled={loading}
              >
                <option value="">Seleziona mese...</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Anno</Form.Label>
              <Form.Select
                value={selectedYear}
                onChange={handleYearChange}
                disabled={loading}
              >
                <option value="">Seleziona anno...</option>
                {getYearOptions().map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Statistics */}
        {totalAttendance > 0 && (
          <div className="mb-4">
            <Row>
              <Col md={4} className="text-center">
                <Badge bg="info" className="p-2">
                  <i className="fas fa-calendar-day me-1"></i>
                  Totale: {totalAttendance}
                </Badge>
              </Col>
              <Col md={4} className="text-center">
                <Badge bg="success" className="p-2">
                  <i className="fas fa-check me-1"></i>
                  Presenti: {presentCount}
                </Badge>
              </Col>
              <Col md={4} className="text-center">
                <Badge bg="danger" className="p-2">
                  <i className="fas fa-times me-1"></i>
                  Assenti: {absentCount}
                </Badge>
              </Col>
            </Row>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Caricamento presenze...</span>
            </Spinner>
          </div>
        )}

        {/* Attendance List */}
        {!loading && (
          <>
            {!selectedMonth || !selectedYear ? (
              <div className="text-center py-4">
                <i className="fas fa-filter fa-3x text-muted mb-3"></i>
                <p className="text-muted">Seleziona mese e anno per visualizzare le presenze</p>
              </div>
            ) : attendance.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                <p className="text-muted">
                  Nessuna presenza trovata per {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </p>
              </div>
            ) : (
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th className="text-center">Presenza</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <strong>{formatDate(record.date)}</strong>
                      </td>
                      <td className="text-center">
                        <Badge bg={record.isPresent ? 'success' : 'danger'}>
                          <i className={`fas ${record.isPresent ? 'fa-check' : 'fa-times'} me-1`}></i>
                          {record.isPresent ? 'Presente' : 'Assente'}
                        </Badge>
                      </td>
                      <td>
                        {record.notes ? (
                          <span className="text-muted">{record.notes}</span>
                        ) : (
                          <span className="text-muted fst-italic">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UserAttendanceModal;